import type { LinkResponse, ListLinksResponse } from '@readlater/shared';
import { ApiError } from './errors';

// Mock data (copied from apps/web/src/lib/mock-data.ts)
const mockLinks: LinkResponse[] = [
  {
    id: 1,
    url: 'https://www.nature.com/articles/d41586-024-00001-0',
    source_domain: 'nature.com',
    favicon_url: 'https://www.google.com/s2/favicons?domain=nature.com&sz=32',
    title: 'AI models are getting better at predicting protein structures',
    content: `# GPT-5 System Card

## Overview
GPT-5 represents a significant leap in reasoning capabilities while maintaining strong safety guarantees.

## Model Variants
- **GPT-5**: Full capability model for complex reasoning
- **GPT-5-mini**: Balanced performance and cost
- **GPT-5-nano**: Fast, efficient for simple tasks

## Key Improvements
1. Enhanced multi-step reasoning
2. Improved instruction following
3. Better calibration of confidence
4. Stronger refusal of harmful requests

## Safety Measures
The model underwent extensive red-teaming and RLHF training to ensure safe behavior across diverse scenarios.`,
    word_count: 1250,
    reading_time: 5,
    summary:
      'New advances in AI-powered protein structure prediction are revolutionizing drug discovery and biological research. The latest models achieve near-experimental accuracy.',
    tags: ['AI', 'Science', 'Biology'],
    content_type: 'article',
    key_quotes: [
      {
        text: 'GPT-5 represents a significant leap in reasoning capabilities',
        context: 'Overview',
      },
      {
        text: 'The model underwent extensive red-teaming and RLHF training',
        context: 'Safety measures',
      },
    ],
    status: 'processed',
    error_message: null,
    is_read: false,
    is_archived: false,
    is_favorite: true,
    created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 min ago
    processed_at: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
    read_at: null,
    archived_at: null,
  },
  {
    id: 2,
    url: 'https://github.com/anthropics/claude-code',
    source_domain: 'github.com',
    favicon_url: 'https://www.google.com/s2/favicons?domain=github.com&sz=32',
    title: 'Claude Code - AI-powered coding assistant',
    content: `# Developer Productivity Metrics

After interviewing engineering leaders at 50+ companies, I've identified metrics that actually correlate with team productivity.

## What Doesn't Work
- Lines of code (trivially gameable)
- Commit count (encourages bad practices)
- Story points (inconsistent across teams)

## What Works
1. **Lead Time**: Time from code commit to production
2. **Deployment Frequency**: How often you ship
3. **Change Failure Rate**: % of deployments causing issues
4. **Mean Time to Recovery**: How fast you fix problems

## Implementation Tips
Start with deployment frequency—it's the easiest to measure and drives improvement in other metrics.`,
    word_count: 800,
    reading_time: 3,
    summary:
      'Claude Code is an AI coding assistant that helps developers write, review, and debug code. It integrates with popular IDEs and supports multiple programming languages.',
    tags: ['Programming', 'AI', 'Tools'],
    content_type: 'article',
    key_quotes: [
      {
        text: "Start with deployment frequency—it's the easiest to measure",
        context: 'Implementation tips',
      },
      { text: 'Lines of code (trivially gameable)', context: "Metrics that don't work" },
    ],
    status: 'processed',
    error_message: null,
    is_read: true,
    is_archived: false,
    is_favorite: false,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    processed_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    read_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    archived_at: null,
  },
  {
    id: 3,
    url: 'https://www.paulgraham.com/read.html',
    source_domain: 'paulgraham.com',
    favicon_url: 'https://www.google.com/s2/favicons?domain=paulgraham.com&sz=32',
    title: 'How to Read',
    content: `## The Art of Reading Well

Reading is not a passive activity. It requires focus, intention, and a willingness to engage deeply with the text. The best readers are those who approach every book, article, or essay with genuine curiosity.

Most people read too fast. They skim the surface, picking up keywords and phrases, but never truly absorbing the ideas underneath. This is especially problematic with dense, idea-rich writing where each paragraph builds on the last.

### Active Reading Strategies

The most effective readers employ a few key strategies:

- **Annotate as you go.** Write in the margins, underline key passages, and jot down questions. This forces you to engage with the material rather than passively consuming it.
- **Pause and reflect.** After each section, stop and ask yourself: what was the main point? Can I explain it in my own words?
- **Connect ideas.** Link what you're reading to things you already know. The best learning happens at the intersection of new and existing knowledge.
- **Re-read difficult passages.** If something doesn't click the first time, slow down. Great writing often rewards a second or third reading.

### Why Speed Reading Fails

> "The person who reads too much and uses their own brain too little falls into lazy habits of thinking." — Albert Einstein

Speed reading promises efficiency, but it delivers superficiality. You can't absorb complex arguments at 800 words per minute. The brain needs time to process, connect, and store information.

Research consistently shows that comprehension drops sharply above 300 words per minute. Beyond that threshold, you're recognizing words without understanding sentences. You're moving your eyes without moving your mind.

### Building a Reading Practice

The best approach is simple: read every day, read widely, and read with attention. Keep a reading journal. Discuss what you read with others. Return to books that challenged you. Over time, your capacity for deep reading will grow — and with it, your capacity for deep thinking.`,
    word_count: 2100,
    reading_time: 8,
    summary:
      'Paul Graham shares insights on effective reading strategies. The key is to read actively, take notes, and focus on understanding rather than completion.',
    tags: ['Productivity', 'Learning', 'Essays'],
    content_type: 'article',
    key_quotes: [
      { text: 'The best way to read is to read actively', context: 'Reading strategy' },
      { text: 'Understanding matters more than speed', context: 'Key insight' },
    ],
    status: 'processed',
    error_message: null,
    is_read: false,
    is_archived: false,
    is_favorite: true,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
    processed_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    read_at: null,
    archived_at: null,
  },
  {
    id: 4,
    url: 'https://example.com/pending-article',
    source_domain: 'example.com',
    favicon_url: 'https://www.google.com/s2/favicons?domain=example.com&sz=32',
    title: null,
    content: null,
    word_count: null,
    reading_time: null,
    summary: null,
    tags: null,
    content_type: null,
    key_quotes: null,
    status: 'processing',
    error_message: null,
    is_read: false,
    is_archived: false,
    is_favorite: false,
    created_at: new Date(Date.now() - 1000 * 60 * 5).toISOString(), // 5 min ago
    processed_at: null,
    read_at: null,
    archived_at: null,
  },
  {
    id: 5,
    url: 'https://arxiv.org/abs/2401.00001',
    source_domain: 'arxiv.org',
    favicon_url: 'https://www.google.com/s2/favicons?domain=arxiv.org&sz=32',
    title: 'Attention Is All You Need: Revisited',
    content: `# Scaling Laws for Neural Language Models

## Abstract
We present a comprehensive survey of scaling laws governing neural language model performance as a function of model size, dataset size, and compute budget.

## Key Findings
1. Loss scales predictably with compute following L = (C/C_0)^{-0.05}
2. Optimal allocation splits compute equally between parameters and data
3. Emergent capabilities appear at specific scale thresholds

## Implications
These findings have profound implications for planning large-scale training runs and predicting model capabilities before training.

## Future Directions
- Investigating scaling laws for multimodal models
- Understanding capability emergence thresholds
- Optimizing for inference cost at scale`,
    word_count: 5000,
    reading_time: 20,
    summary:
      'A comprehensive review of transformer architectures and their impact on modern AI. The paper analyzes improvements since the original attention mechanism.',
    tags: ['Research', 'AI', 'Deep Learning'],
    content_type: 'pdf',
    key_quotes: [
      {
        text: 'Emergent capabilities appear at specific scale thresholds',
        context: 'Key findings',
      },
      {
        text: 'Optimal allocation splits compute equally between parameters and data',
        context: 'Compute allocation',
      },
    ],
    status: 'processed',
    error_message: null,
    is_read: true,
    is_archived: true,
    is_favorite: false,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(), // 1 week ago
    processed_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    read_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    archived_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
  },
  {
    id: 6,
    url: 'https://broken-site.com/article',
    source_domain: 'broken-site.com',
    favicon_url: null,
    title: 'https://broken-site.com/article',
    content: null,
    word_count: null,
    reading_time: null,
    summary: null,
    tags: null,
    content_type: null,
    key_quotes: null,
    status: 'failed',
    error_message: 'Failed to extract content: Connection timeout',
    is_read: false,
    is_archived: false,
    is_favorite: false,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(), // 3 hours ago
    processed_at: null,
    read_at: null,
    archived_at: null,
  },
];

// In-memory state for mock data (persists during session)
let mockLinksState = [...mockLinks];
let mockTimers: ReturnType<typeof setTimeout>[] = [];

function getMockLinks(): LinkResponse[] {
  return mockLinksState;
}

function updateMockLink(id: number, updates: Partial<LinkResponse>): LinkResponse | null {
  const index = mockLinksState.findIndex((l) => l.id === id);
  if (index === -1) return null;

  mockLinksState[index] = { ...mockLinksState[index], ...updates };
  return mockLinksState[index];
}

function addMockLink(url: string): LinkResponse {
  const newLink: LinkResponse = {
    id: Date.now(),
    url,
    source_domain: new URL(url).hostname,
    favicon_url: `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`,
    title: null,
    content: null,
    word_count: null,
    reading_time: null,
    summary: null,
    tags: null,
    content_type: null,
    key_quotes: null,
    status: 'pending',
    error_message: null,
    is_read: false,
    is_archived: false,
    is_favorite: false,
    created_at: new Date().toISOString(),
    processed_at: null,
    read_at: null,
    archived_at: null,
  };

  mockLinksState = [newLink, ...mockLinksState];

  // Simulate processing after 1 second
  mockTimers.push(
    setTimeout(() => {
      const idx = mockLinksState.findIndex((l) => l.id === newLink.id);
      if (idx !== -1) {
        mockLinksState[idx] = {
          ...mockLinksState[idx],
          status: 'processing',
        };
      }
    }, 1000)
  );

  // Simulate completion after 5 seconds
  mockTimers.push(
    setTimeout(() => {
      const idx = mockLinksState.findIndex((l) => l.id === newLink.id);
      if (idx !== -1) {
        mockLinksState[idx] = {
          ...mockLinksState[idx],
          status: 'processed',
          title: `Article from ${new URL(url).hostname}`,
          summary:
            'This is a mock summary for the saved link. In production, this would be generated by AI.',
          tags: ['Mock', 'Test'],
          reading_time: 5,
          word_count: 1000,
          processed_at: new Date().toISOString(),
        };
      }
    }, 5000)
  );

  return newLink;
}

function deleteMockLink(id: number): boolean {
  const index = mockLinksState.findIndex((l) => l.id === id);
  if (index === -1) return false;

  mockLinksState = mockLinksState.filter((l) => l.id !== id);
  return true;
}

function getMockLinksResponse(params?: {
  is_read?: boolean;
  is_archived?: boolean;
  is_favorite?: boolean;
}): ListLinksResponse {
  let filtered = mockLinksState;

  if (params?.is_read !== undefined) {
    filtered = filtered.filter((l) => l.is_read === params.is_read);
  }
  if (params?.is_archived !== undefined) {
    filtered = filtered.filter((l) => l.is_archived === params.is_archived);
  }
  if (params?.is_favorite !== undefined) {
    filtered = filtered.filter((l) => l.is_favorite === params.is_favorite);
  }

  return {
    links: filtered,
    pagination: {
      limit: 50,
      offset: 0,
      total: filtered.length,
    },
  };
}

// Mock request handler (extracted from apps/web/src/lib/api-client.ts handleMockRequest)
export function handleMockRequest<T>(endpoint: string, options: RequestInit = {}): T {
  const method = options.method || 'GET';
  const body = options.body ? JSON.parse(options.body as string) : undefined;

  const [path, queryString] = endpoint.split('?');
  const params = new URLSearchParams(queryString || '');

  // GET /links
  if (method === 'GET' && path === '/links') {
    const is_read = params.get('is_read');
    const is_archived = params.get('is_archived');
    const is_favorite = params.get('is_favorite');

    return getMockLinksResponse({
      is_read: is_read ? is_read === 'true' : undefined,
      is_archived: is_archived ? is_archived === 'true' : undefined,
      is_favorite: is_favorite ? is_favorite === 'true' : undefined,
    }) as T;
  }

  // POST /links
  if (method === 'POST' && path === '/links') {
    const newLink = addMockLink(body.url);
    return newLink as T;
  }

  // Routes with /links/:id pattern
  const linkMatch = path.match(/^\/links\/(\d+)$/);
  if (linkMatch) {
    const id = parseInt(linkMatch[1], 10);

    if (method === 'GET') {
      const link = getMockLinks().find((l) => l.id === id);
      if (!link) {
        throw new ApiError('Link not found', 404, 'NOT_FOUND');
      }
      return link as T;
    }

    if (method === 'PATCH') {
      const updated = updateMockLink(id, body);
      if (!updated) {
        throw new ApiError('Link not found', 404, 'NOT_FOUND');
      }
      return updated as T;
    }

    if (method === 'DELETE') {
      const deleted = deleteMockLink(id);
      if (!deleted) {
        throw new ApiError('Link not found', 404, 'NOT_FOUND');
      }
      return undefined as T;
    }
  }

  // POST /users/push-token
  if (method === 'POST' && path === '/users/push-token') {
    return { success: true } as T;
  }

  // DELETE /users/push-token
  if (method === 'DELETE' && path === '/users/push-token') {
    return undefined as T;
  }

  throw new ApiError(`Mock not implemented for ${method} ${endpoint}`, 501, 'NOT_IMPLEMENTED');
}

// Reset mock state (useful for tests)
export function resetMockState(): void {
  mockTimers.forEach(clearTimeout);
  mockTimers = [];
  mockLinksState = [...mockLinks];
}
